/**
 * Integrytas Group — PFW Third-Party Portal: data access using the portal token.
 *
 * SEPARATE from apiDb (src/lib/api_client.ts), which authenticates with the app's
 * GoTrue session or the anon key. A portal user has neither — its identity is a
 * scoped JWT (see portal_auth.ts) carrying { portal_user_id, pfw_company_id }
 * claims. This client forwards that token as the caller's bearer to the SAME
 * gateway; the gateway simply passes the Authorization header through to
 * PostgREST, so RLS + the pfw_current_* claim helpers resolve exactly as tested
 * against the gateway directly. No gateway changes were needed for this.
 */
import { loadPortalSession } from './portal_auth';

const SUPABASE_URL      = import.meta.env.VITE_SUPABASE_URL ?? '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';
const GATEWAY_URL       = `${SUPABASE_URL}/functions/v1/csm-api`;

export interface PortalApiResult<T = unknown> { data: T | null; error: string | null; }

async function portalGatewayFetch<T>(body: Record<string, unknown>): Promise<PortalApiResult<T>> {
  const session = loadPortalSession();
  if (!session) return { data: null, error: 'PFW.PORTAL_NOT_SIGNED_IN' };
  try {
    const resp = await fetch(GATEWAY_URL, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${session.token}`,
        'x-csm-client': 'pfw-portal',
      },
      body: JSON.stringify(body),
    });
    const json = await resp.json().catch(() => ({ data: null, error: `PFW.PORTAL_BAD_RESPONSE_${resp.status}` }));
    if (!resp.ok || json.error) return { data: null, error: json.error ?? `PFW.PORTAL_HTTP_${resp.status}` };
    return { data: (json.data ?? null) as T, error: null };
  } catch {
    return { data: null, error: 'PFW.PORTAL_NETWORK_ERROR' };
  }
}

export function portalRpc<T = unknown>(name: string, args: Record<string, unknown> = {}): Promise<PortalApiResult<T>> {
  return portalGatewayFetch<T>({ kind: 'rpc', name, args });
}

export function portalSelect<T = unknown>(table: string, columns: string, filters?: { col: string; op: string; value: unknown }[]): Promise<PortalApiResult<T>> {
  return portalGatewayFetch<T>({ kind: 'select', table, columns, filters: filters ?? [] });
}
